import { useEffect, useState } from 'react';
import { ArrowRight, CalendarDays, ChartNoAxesCombined, ChevronRight, Compass, LockKeyhole } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useHubCatalog } from '../../hooks/useHubCatalog';
import { fetchCourseItems, fetchCourseProgress, ownsVolume } from '../../lib/hub-api';
import { VolumeCard } from '../../components/VolumeCard';

export function DashboardPage() {
  const { profile, user } = useAuth();
  const { volumes, entitlements, loading, error } = useHubCatalog();
  const [courseProgress, setCourseProgress] = useState({ completed: 0, total: 0, percentage: 0 });
  const owned = volumes.filter((volume) => ownsVolume(entitlements, volume.id));
  const clarity = volumes.find((volume) => volume.slug === 'clarity');
  const clarityOwned = clarity ? ownsVolume(entitlements, clarity.id) : false;
  const firstName = profile?.full_name?.split(' ')[0] || 'Member';

  useEffect(() => {
    if (!clarity || !clarityOwned || !user) return;
    (async () => {
      try {
        const items = await fetchCourseItems(clarity.id);
        const progress = await fetchCourseProgress(user.id, items.map((item) => item.id));
        const completed = progress.filter((entry) => entry.completed_at).length;
        setCourseProgress({ completed, total: items.length, percentage: items.length ? Math.round((completed / items.length) * 100) : 0 });
      } catch { setCourseProgress({ completed: 0, total: 0, percentage: 0 }); }
    })();
  }, [clarity, clarityOwned, user]);

  if (loading) return <div className="page-loading"><span className="loader" />Loading your rebuilds…</div>;

  return (
    <div className="hub-page">
      <section className="dashboard-hero charcoal-card">
        <div>
          <p className="eyebrow light">Welcome back, {firstName}</p>
          <h1>{clarityOwned ? 'Continue the Clarity Rebuild.' : 'Your hub is ready.'}</h1>
          <p>{clarityOwned
            ? 'The Story and lesson player is live. Return to the exact course item you were working through.'
            : 'Explore the four-volume path. A purchased rebuild will unlock inside this same account.'}</p>
          <Link className="button clay" to={clarityOwned ? '/hub/rebuilds/clarity/course' : '/hub/rebuilds'}>
            {clarityOwned ? 'Continue the Clarity Rebuild' : 'View the Rebuild Series'} <ArrowRight size={17} />
          </Link>
        </div>
        <div className="hero-metrics">
          <article><strong>{owned.length}/4</strong><span>Rebuilds unlocked</span></article>
          <article><strong>{courseProgress.percentage}%</strong><span>Clarity learned</span></article>
          <article><strong>90</strong><span>Day rebuild arc</span></article>
        </div>
      </section>

      {error && <div className="error-banner">{error}</div>}

      <section className="section-heading">
        <div><p className="eyebrow">The Rebuild Series</p><h2>One Hub. Four Rebuilds.</h2></div>
        <Link to="/hub/rebuilds">View all <ChevronRight size={17} /></Link>
      </section>
      <div className="volume-grid dashboard-volumes">
        {volumes.map((volume) => <VolumeCard key={volume.id} volume={volume} owned={ownsVolume(entitlements, volume.id)} compact />)}
      </div>

      <section className="dashboard-grid">
        <article className="card parchment-card">
          <div className="icon-tile"><Compass /></div><p className="eyebrow">Learn</p><h3>Course progress</h3>
          <p>{clarityOwned ? 'Stories, lessons, format preferences, notes, saves, and completion are connected.' : 'Unlock Clarity to begin tracking course progress.'}</p>
          <div className="empty-meter"><span style={{ width: `${courseProgress.percentage}%` }} /></div><small>{courseProgress.completed} of {courseProgress.total} course items complete.</small>
          {clarityOwned && <Link className="text-link" to="/hub/rebuilds/clarity/course">Open the player <ArrowRight size={15} /></Link>}
        </article>
        <article className="card parchment-card">
          <div className="icon-tile"><ChartNoAxesCombined /></div><p className="eyebrow">Build</p><h3>Anchors and Blueprint</h3>
          <p>Your Workbook App, anchor history, and Blueprint arrive in Phase 3.</p>
          <Link className="text-link" to="/hub/workbook">See the reserved workspace <ArrowRight size={15} /></Link>
        </article>
        <article className="card parchment-card">
          <div className="icon-tile"><CalendarDays /></div><p className="eyebrow">Live</p><h3>90-Day Rebuild</h3>
          <p>Nine module weeks followed by the 30-Day Continuation Plan.</p>
          <Link className="text-link" to="/hub/rebuild-map">View the planned system <ArrowRight size={15} /></Link>
        </article>
      </section>

      {!owned.length && (
        <section className="locked-account card">
          <LockKeyhole />
          <div><h3>No paid rebuild is attached to this account yet.</h3><p>Once a purchase is verified, its entitlement will open automatically inside this hub.</p></div>
          <Link className="button secondary" to="/hub/rebuilds">Explore the series</Link>
        </section>
      )}
    </div>
  );
}
